import Mousetrap from 'mousetrap';
import config from '../config';
import type { SVGOptions } from '../uTheory.Notation/SVG';
import SVGContext from '../uTheory.Notation/SVG';
import utils from '../utils';
import { getCssProperty } from './getCssProperty';

const colors = config.Colors;
/**
 * A replacement slider interface, styled for uTheory
 */

/* eslint-disable no-unused-vars */

const height = 60;
const trackHeight = height / 8;
const circleDiameter = height / 1.75;
const activeCircleDiameter = circleDiameter * 1.2;
const tickHeight = circleDiameter * 0.66;
const tickWidth = trackHeight / 2;
const tickOffset = 0; // Y offset from center

const xSymbol = Symbol('x');
const valueSymbol = Symbol('value');
const disabled = Symbol('disabled');

export interface SliderOptions extends SVGOptions {
  /*  default auto */
  width?: number;
  /*  [min, max] default [0, 100] */
  range?: number[];
  /*  starting value. default min; if options.values, this must be within that range */
  value?: number | any;
  /*  recurring array of intervals: [0, 1, 2] => 0, 1, 3, 4, 6, etc */
  steps?: number[];
  /**
   * recurring array of intervals; omit for no tickmarks
   * if a zero is present, it's only interpreted the first time.
   */
  ticks?: number[];
  /*  only valid if it contains one value per possible step. */
  values?: any[];
  /* Callback when the value changes */
  onChange?: Function;
}

/**
 * A replacement slider interface, styled for uTheory
 * @class
 * @param {HTMLDivElement} div the containing DOM element
 * @param {object} options
 * @param {number} options.width default auto
 * @param {number[]} options.range [min, max] default [0, 100]
 * @param {number|Any} options.value default min; if options.values, this must be in that
 * @param {number[]} options.steps recurring array of intervals: [0, 1, 2] => 0, 1, 3, 4, 6, etc
 *   if a zero is present, it's only interpreted the first time.
 * @param {number[]} options.ticks recurring array of intervals; omit for no tickmarks
 * @param {Any[]} options.values only valid if it contains one value per possible step.
 *  when omitted, the simple numeric value along slider is returned.
 *
 * Listeners:
 * @param {Function} options.onChange function to call back when change happens.
 *
 * @property {number[]} tickCoordinates x-based coordinates for each tick
 * @property {number[]} stepCoordinates x-based coordinates for each step
 * @property {number[]} stepValues number based values for each step
 */
export default class Slider extends SVGContext {
  declare options: SliderOptions;

  declare height: number;

  div: HTMLDivElement;

  stepValues: number[];

  tickCoordinates: number[];

  stepCoordinates: number[];

  xMin: number;

  xMax: number;

  trackWidth: number;

  circleEl: SVGCircleElement;

  red: string = colors.red;

  orange: string = colors.orange;

  green: string = colors.green;

  constructor(div: HTMLDivElement, options: SliderOptions = {}) {
    super();
    options = {
      range: [0, 100],
      onChange: () => {},
      ...options,
    };

    this.red = getCssProperty('--red-500-as-dark-fg') || colors.red;
    this.orange = getCssProperty('--button-color') || colors.orange;
    this.green = getCssProperty('--app-theme-contrast') || colors.green;

    // hold value & set it later:
    const value = typeof options.value !== 'undefined' ? options.value : options.range[0];
    this[valueSymbol] = value;

    delete options.value;
    this.options = options;
    this.div = div;
    Object.assign(this, options, { div });
    this.height = height;
    div.appendChild(this.svg);

    this.stepValues = this.stepsToPoints(this.options.steps);

    this.redraw();
    this.makeInteractive();
    this.updateAriaAttributes();
    this.addListeners();

    // this.overlayDiv.classList.add('utheory-slider-element');
  }

  private updateAriaAttributes() {
    this.overlayDiv.setAttribute('tabindex', '-1');
    this.svg.setAttribute('tabindex', '-1');
    this.div.setAttribute('role', 'slider');
    this.div.setAttribute('tabindex', '0');
    this.div.setAttribute('aria-valuemin', `${this.options.range[0]}`);
    this.div.setAttribute('aria-valuemax', `${this.options.range[1]}`);
    this.div.setAttribute('aria-valuenow', `${this.x}`);
    this.div.setAttribute('aria-valuetext', `${this.value}`);
  }

  private addListeners() {
    const mousetrap = Mousetrap(this.div);
    mousetrap.bind(['right', 'up'], (evt) => {
      evt.preventDefault();
      this.increment();
    });
    mousetrap.bind(['left', 'down'], (evt) => {
      evt.preventDefault();
      this.decrement();
    });

    utils.createClass(
      'time-signature-slider:focus',
      `box-shadow: 0px 0px 2px 1px ${colors.selected}`
    );
    this.overlayDiv.addEventListener('click', () => {
      this.div.focus();
    });
  }

  increment() {
    if (!this.options.steps) {
      this.x++;
      return;
    }

    let closestStepIndex = (utils.closest(this.x, this.stepCoordinates) as number) + 1;
    closestStepIndex = Math.min(closestStepIndex, this.stepCoordinates.length - 1);
    closestStepIndex = Math.max(closestStepIndex, 0);
    this.x = this.stepCoordinates[closestStepIndex];
  }

  decrement() {
    if (!this.options.steps) {
      this.x--;
      return;
    }

    let closestStepIndex = (utils.closest(this.x, this.stepCoordinates) as number) - 1;
    closestStepIndex = Math.min(closestStepIndex, this.stepCoordinates.length - 1);
    closestStepIndex = Math.max(closestStepIndex, 0);
    this.x = this.stepCoordinates[closestStepIndex];
  }

  redraw() {
    this.width = this.options.width || this.div.clientWidth;
    this.clear();
    const value = this[valueSymbol];
    this.resize(this.width, this.height);
    // This is the range of motion of circle center
    this.trackWidth = this.width - activeCircleDiameter;
    this.xMin = activeCircleDiameter / 2;
    this.xMax = this.width - activeCircleDiameter / 2;

    this.stepCoordinates = this.stepValues.map(this.rangeValueToX.bind(this));
    this.tickCoordinates = this.stepsToPoints(this.options.ticks || []).map(
      this.rangeValueToX.bind(this)
    );

    this.svg.style.width = '100%';
    this[xSymbol] = this.valueToX(value);
    this.drawTrack();
    this.drawCircle();
  }

  /**
   * Convert a list of recurring intervals to a list of all possible points
   * @param {number[]} steps list of intervals at which to put points in range
   * @returns {number[]} array of points in range
   */
  private stepsToPoints(steps: number[]): number[] {
    if (!steps) return [];
    const [min, max] = this.options.range;
    let lastPoint = min;
    const points = [];
    if (steps[0] === 0) {
      points.push(min);
      steps.splice(0, 1);
    }

    // Adds points along indicated spacing
    function morePoints() {
      steps.forEach((step) => {
        lastPoint += step;
        if (lastPoint <= max) points.push(lastPoint);
      });
    }

    while (!points.length || lastPoint < max) {
      morePoints();
    }

    return points;
  }

  get value() {
    return this.xToValue(this[xSymbol]);
  }

  set value(val) {
    // if it === previous, return!
    if (this[valueSymbol] === val) return;
    this[valueSymbol] = val;
    this.options.onChange(val);
    this.x = this.valueToX(val);
  }

  get x(): number {
    return this[xSymbol];
  }

  set x(x: number) {
    if (this[xSymbol] === x) return;
    x = Math.max(this.xMin, x);
    x = Math.min(this.xMax, x);
    this[xSymbol] = x;
    this.moveCircle();
    const newValue = this.xToValue(x);
    if (this[valueSymbol] !== newValue) {
      this[valueSymbol] = this.xToValue(x);
      this.options.onChange(newValue);
    }
    this.updateAriaAttributes();
  }

  set disabled(bool) {
    this[disabled] = !!bool;
    this.svg.style.filter = bool ? 'grayscale(100%)' : '';
    this.div.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
  }

  get disabled() {
    return !!this[disabled];
  }

  /**
   * converts an index of steps (if steps given) to a number in min, max range
   * @param {number} index
   * @returns {number} within range
   */
  /*
  private stepIndexToRange(index: number): number {
    if (!this.options.steps) return index;
    return this.stepValues[index];
  }
  */

  private valueToX(value: any): number {
    const range = this.valueToRange(value);
    return this.rangeValueToX(range);
  }

  /**
   * converts a value to a rangeValue, or returns value if this.values not defined.
   *
   * @param {any} value
   */
  private valueToRange(value) {
    if (!this.options.values) return value;
    const i = this.options.values.indexOf(value);
    return this.stepValues[i];
  }

  /**
   * converts a range to an X coordinate
   *
   * @param {number} value numerical value within given min, max range
   */
  private rangeValueToX(value): number {
    if (utils.typeof(value) !== 'number') throw new Error('value must be a number.');
    const [min, max] = this.options.range;
    const range = max - min;

    value -= min;
    const coefficient = value / range;
    const x = this.trackWidth * coefficient + activeCircleDiameter / 2;
    return x;
  }

  /**
   * converts an X coordinate to a value within given min, max range. Use xToStep for snapping.
   *
   * @param {number} x an x-coordinate value
   * @returns {number} within min, max range
   */
  private xToRange(x): number {
    const [min, max] = this.options.range;
    const range = max - min;

    const minX = activeCircleDiameter / 2;
    const coefficient = (x - minX) / this.trackWidth;
    const value = range * coefficient + min;
    return value;
  }

  private xToRangeStep(x) {
    if (!this.options.steps) return this.xToRange(x);
    const rangeValue = this.xToRange(x);
    const index = utils.closest(rangeValue, this.stepValues);
    return this.stepValues[index];
  }

  private rangeStepToValue(range) {
    if (!this.options.values) return range;
    const index = utils.closest(range, this.stepValues);
    return this.options.values[index];
  }

  private xToValue(x: number) {
    const rangeValue = this.xToRangeStep(x);
    return this.rangeStepToValue(rangeValue);
  }

  drawTrack() {
    const trackMargin = activeCircleDiameter - circleDiameter;
    this.line(trackMargin, this.height / 2, this.width - trackMargin, this.height / 2, {
      'stroke-width': trackHeight,
      stroke: this.green,
      fill: 'none',
      'stroke-linecap': 'round',
    });

    const tickTop = this.height / 2 - tickHeight / 2 + tickOffset;
    const tickBottom = this.height / 2 + tickHeight / 2 + tickOffset;
    this.tickCoordinates.forEach((x) => {
      this.line(x, tickTop, x, tickBottom, {
        'stroke-width': tickWidth,
        stroke: this.green,
        fill: 'none',
      });
    });
  }

  private drawCircle() {
    const x = this.x;
    const y = height / 2;
    const radius = circleDiameter / 2;
    this.circle(x, y, radius, {
      stroke: 'none',
      fill: this.orange,
    });
    this.circleEl = this.lastChild as SVGCircleElement;
  }

  private moveCircle() {
    if (!this.circleEl) return; // we haven't drawn it yet.
    this.circleEl.setAttribute('cx', `${this.x}`);
  }

  touchStart(e, coords) {
    if (this[disabled]) return;
    this.circleEl.style.transition =
      'cx 0.1s ease-in, fill 0.1s ease-in 0.08s, r 0.1s ease-in 0.08';
    this.circleEl.setAttribute('fill', this.red);
    this.circleEl.setAttribute('r', `${activeCircleDiameter / 2}`);
    e.preventDefault();
    this.x = coords.x;
  }

  drag(e, coords) {
    if (this[disabled]) return;
    this.circleEl.style.transition = '';
    e.preventDefault();
    this.x = coords.x;
  }

  touchEnd(e, coords) {
    if (this[disabled]) return;
    e.preventDefault();
    this.circleEl.style.transition = 'cx 0.1s ease-out, fill 0.1s ease-out, r 0.1s ease-out';
    this.circleEl.setAttribute('fill', this.orange);
    this.circleEl.setAttribute('r', `${circleDiameter / 2}`);
    const range = this.xToRangeStep(coords.x);
    this.x = this.rangeValueToX(range);
  }
}
